@if (loading) {
<spinner-overlay [fullscreen]="true"></spinner-overlay>
}
<app-menu
    [organisation]="organisation"
    [screen]="screen"
    [searchNodes]="searchNodes"
    (menuEvent)="handleFunctionEvent($event)"
    (selectNodeEvent)="findNode($event)"
></app-menu>
<app-analysis-header [organisation]="organisation" [view]="lensKey" [canShowFullModel]="true" [(fullModel)]="fullModel"></app-analysis-header>

@if (isHelpOn()) {
<div class="hint">
    @if (lensKey === 'people') {
    Shows how roles hand work to each other. Each box is a role and the actions it takes in a row before handing the work
    on; a role shows once for each separate stretch of work. The band across the top shows which models the work is in.
    } @else {
    Shows how information moves between systems. Each box is a system and the actions done in it in a row before the
    information passes to another system; dashed boxes are work done outside any system. Solid teal lines are automated
    interfaces; dashed orange lines are where people move the information by hand.
    }
</div>
}

<mat-sidenav-container class="drawer-container">
    <mat-sidenav
        mode="side"
        [opened]="true"
        position="start"
        class="analysis-sidebar"
        disableClose="true"
    >
        @if (fullModel) {
        <section class="sidebar-section">
            <h3>Full model</h3>
            <p class="sidebar-note">Every action, information node, system and role in the model, as drawn in LINQ. Double-click a node for its details. "View summary" returns to the {{ lensKey === 'people' ? 'roles' : 'systems' }} view.</p>
        </section>
        } @else if (organisation && hasReport) {
        <section id="lens-report" class="sidebar-section">
            @if (lensKey === 'people') {
            <h3>Workforce report</h3>
            <p class="sidebar-note">Workload, cost and capacity for every role and person, with what to look at first.</p>
            } @else {
            <h3>Systems report</h3>
            <p class="sidebar-note">Cost, carbon and interfaces for every system and piece of infrastructure, and where automation could help.</p>
            }
            <button
                mat-flat-button
                color="accent"
                (click)="rightdrawer.toggle()"
                [attr.aria-expanded]="rightdrawer.opened"
                data-testid="open-report"
            >
                <mat-icon>{{ rightdrawer.opened ? 'close' : lensKey === 'people' ? 'groups' : 'dns' }}</mat-icon>{{ rightdrawer.opened ? 'Close the report' : lensKey === 'people' ? 'Open workforce report' : 'Open systems report' }}
            </button>
        </section>

        <section id="lens-models" class="sidebar-section">
            <h3>Models shown</h3>
            @if (selectedModels.length > 12) {
            <p class="sidebar-note legend-note">{{ selectedModels.length }} models. Choose fewer at the top to tell them apart.</p>
            } @else if (!selectedModels.length) {
            <p class="sidebar-note legend-note">None of the models chosen has actions in this workspace.</p>
            } @else {
            <ul class="model-legend" aria-label="Model colours">
                @for (model of models; track model.key) {
                @if (selectedModels.includes(model.key)) {
                <li><span class="swatch" [style.background]="model.colour"></span>{{ model.name }}</li>
                }
                }
            </ul>
            }
        </section>

        <section id="lens-colour" class="sidebar-section">
            <h3>Colour {{ lensKey === 'people' ? 'roles' : 'systems' }} by</h3>
            <mat-button-toggle-group [value]="colourBy" (change)="colourByChanged($event.value)" class="colour-by" hideSingleSelectionIndicator
                [attr.aria-label]="'Colour ' + (lensKey === 'people' ? 'roles' : 'systems') + ' by'" data-testid="lens-colour-by">
                @for (option of colourOptions; track option.value) {
                <mat-button-toggle [value]="option.value">{{ option.label }}</mat-button-toggle>
                }
            </mat-button-toggle-group>
            @if (colourBy === 'status') {
            <ul class="status-legend">
                @for (status of legendStatuses; track status) {
                <li><span class="dot" [style.background]="statusColours[status]"></span>{{ statusLabel(status) }}</li>
                }
                <li><span class="dashed"></span>Done by systems</li>
                <li><span class="pill"></span>Output</li>
            </ul>
            } @else if (colourBy === 'automation') {
            <ul class="status-legend">
                @for (a of legendAutomation; track a) {
                <li><span class="dot" [style.background]="automationColours[a]"></span>{{ automationLabel(a) }}</li>
                }
            </ul>
            } @else if (colourBy === 'hours' || colourBy === 'cost' || colourBy === 'carbon') {
            <div class="legend-bar" [style.background]="'linear-gradient(to right, ' + (lensKey === 'people' ? hoursRamp : systemsRamp).join(', ') + ')'"></div>
            <div class="legend-scale"><span>{{ colourBy === 'hours' ? 'Fewer hours a year' : colourBy === 'cost' ? 'Less cost a year' : 'Less carbon a year' }}</span><span>More</span></div>
            }
            @if (lensKey === 'systems') {
            <h4>Interfaces</h4>
            <ul class="status-legend">
                @for (k of legendInterfaces; track k) {
                <li><span class="line" [class.dashed-line]="k === 'manual'" [style.border-color]="interfaceColours[k]"></span>{{ interfaceLabel(k) }}</li>
                }
                <li><span class="dashed"></span>Work outside systems</li>
                <li><span class="pill"></span>Output</li>
            </ul>
            }
        </section>

        <section id="lens-simulation" class="sidebar-section">
            <h3>Simulation</h3>
            <p class="sidebar-note">Runs work through the actions and shows where it waits{{ lensKey === 'people' ? ' for each role' : ', system by system' }}.</p>
            @if (simulation?.running) {
            <button mat-flat-button color="accent" (click)="stopSimulation()" data-testid="lens-stop-simulation">
                <mat-icon>stop</mat-icon>Stop simulation
            </button>
            } @else if (simulation) {
            <div class="simulation-actions">
                @if (!simulation.finished) {
                <button mat-stroked-button color="accent" (click)="continueSimulation()"><mat-icon>play_arrow</mat-icon>Continue</button>
                }
                <button mat-stroked-button color="accent" (click)="runSimulation()"><mat-icon>replay</mat-icon>Start again</button>
                <button mat-stroked-button (click)="clearSimulation()"><mat-icon>clear</mat-icon>Clear</button>
            </div>
            } @else {
            <button mat-flat-button color="accent" (click)="runSimulation()" [disabled]="!graphNodes.length" data-testid="lens-run-simulation">
                <mat-icon>play_arrow</mat-icon>Run simulation
            </button>
            }
            <button mat-stroked-button class="what-if" (click)="openWhatIf()" [disabled]="!graphNodes.length" data-testid="open-what-if"
                matTooltip="Change the load or the people on a role, see what the roles need, and simulate it against today">
                <mat-icon>tune</mat-icon>What if…
            </button>
            @if (progress; as p) {
            <p class="simulation-progress" role="status">
                Week {{ p.week }}, day {{ p.day }}{{ p.finished ? ' (finished)' : '' }}: {{ count(p.received) }} received,
                {{ count(p.produced) }} produced, {{ count(p.waiting) }} waiting
            </p>
            }
            @if (waiting.length) {
            <h4>Where work waits</h4>
            <ol class="waiting">
                @for (w of waiting; track w.run.key) {
                <li><button type="button" class="link" (click)="focus(w.run)">{{ w.run.name }}</button><span>{{ count(w.queue) }}</span></li>
                }
            </ol>
            }
        </section>
        }
    </mat-sidenav>

    <mat-sidenav-content class="lens-content">
        @if (fullModel) {
        <app-full-model [organisation]="organisation" [scope]="currentScope"></app-full-model>
        } @else {
        @if (error) {
        <p class="error" role="alert">{{ error }}</p>
        } @else if (!loading && !graphNodes.length) {
        <p class="empty">No actions in the models shown, so there is no work to show.</p>
        }
        <app-lens-graph [nodes]="graphNodes" [edges]="graphEdges" [orientation]="orientation" [title]="lensKey === 'people' ? 'Role model' : 'Systems model'"
            [subtitle]="lensKey === 'people' ? 'How roles hand work to each other' : 'How information moves between systems'" (graphBuilt)="graphBuilt($event)" (nodeSelected)="select($event)"></app-lens-graph>

        @if (role; as run) {
        <aside class="detail" aria-label="Selected role" data-testid="lens-detail">
            <header>
                <div>
                    <h3>{{ run.name }}</h3>
                    @if (run.automated) {
                    <p class="muted">Done by systems</p>
                    } @else {
                    <p class="status"><span class="dot" [style.background]="statusColours[run.status]"></span>{{ statusLabel(run.status) }}
                        @if (run.utilisation !== null) {<span class="muted">· {{ percent(run.utilisation) }} of capacity{{ run.capacityFromDefault ? ' (full-time year)' : '' }}</span>}
                    </p>
                    }
                </div>
                <button mat-icon-button aria-label="Close" (click)="select(undefined)"><mat-icon>close</mat-icon></button>
            </header>
            <dl class="figures">
                <div><dt>Here</dt><dd>{{ hours(run.hours) }} h a year</dd></div>
                @if (!run.automated) {
                <div><dt>Whole role</dt><dd>{{ run.capacity !== null ? hours(run.capacity) + ' h capacity' : 'Capacity not set' }}</dd></div>
                <div><dt>People</dt><dd>{{ run.headcount }}</dd></div>
                }
            </dl>

            <h4>Actions, in order</h4>
            <ol class="actions">
                @for (name of run.actionNames; track $index) {<li>{{ name }}</li>}
            </ol>

            <ng-container *ngTemplateOutlet="modelShares; context: { run: run }"></ng-container>

            @if (!run.automated) {
            <h4>Who fills the role</h4>
            @if (people.length) {
            <ul class="people">
                @for (person of people; track person.key) {
                <li>
                    <span class="dot" [style.background]="statusColours[person.status]" [attr.aria-label]="statusLabel(person.status)"></span>
                    <span class="name">{{ person.name }}{{ person.team ? ' (team of ' + person.headcount + ')' : '' }}</span>
                    <span class="muted">{{ percent(person.utilisation) }}</span>
                </li>
                }
            </ul>
            } @else {
            <p class="muted">No one is assigned to this role.</p>
            }
            }

            <h4>Hand-overs</h4>
            <ng-container *ngTemplateOutlet="links; context: { kinds: false }"></ng-container>
            <ng-container *ngTemplateOutlet="createsList"></ng-container>
        </aside>
        }

        @if (system; as run) {
        <aside class="detail" aria-label="Selected system" data-testid="lens-detail">
            <header>
                <div>
                    <h3>{{ run.name }}@if (run.aiAgent) {<span class="tag ai">AI agent</span>}</h3>
                    <p class="status"><span class="dot" [style.background]="automationColours[run.automation]"></span>{{ automationLabel(run.automation) }}
                        @if (run.systemKeys.length && run.automation !== 'automated') {<span class="muted">· {{ percent(run.automatedShare) }} of its actions automated</span>}
                    </p>
                </div>
                <button mat-icon-button aria-label="Close" (click)="select(undefined)"><mat-icon>close</mat-icon></button>
            </header>
            <dl class="figures">
                <div><dt>People's time</dt><dd>{{ hours(run.hours) }} h a year</dd></div>
                <div><dt>Cost here</dt><dd>{{ money(run.cost) }} a year</dd></div>
                <div><dt>Carbon here</dt><dd>{{ count(run.carbon) }} kg CO2e</dd></div>
            </dl>

            <h4>Actions, in order</h4>
            <ol class="actions">
                @for (name of run.actionNames; track $index) {
                <li>{{ name }} <span class="level"><span class="dot small" [style.background]="automationColours[run.actionLevels[$index]]"></span>{{ levelLabel(run.actionLevels[$index]) }}</span></li>
                }
            </ol>

            <ng-container *ngTemplateOutlet="modelShares; context: { run: run }"></ng-container>

            @if (run.roleNames.length) {
            <h4>{{ run.systemKeys.length ? 'Who works in it' : 'Who does the work' }}</h4>
            <p>{{ run.roleNames.join(', ') }}</p>
            }

            @if (systemSummaries.length) {
            <h4>Across all its work</h4>
            <ul class="shares">
                @for (s of systemSummaries; track s.key) {
                <li><span class="name">{{ s.name }}</span><span class="muted">{{ money(s.cost) }} · {{ count(s.carbon) }} kg CO2e · {{ s.models.length }} {{ s.models.length === 1 ? 'model' : 'models' }}</span></li>
                @if (s.infrastructure.length) {<li class="sub">Runs on {{ s.infrastructure.join(', ') }}</li>}
                }
            </ul>
            }

            <h4>Interfaces</h4>
            <ng-container *ngTemplateOutlet="links; context: { kinds: true }"></ng-container>
            <ng-container *ngTemplateOutlet="createsList"></ng-container>
        </aside>
        }

        <ng-template #modelShares let-run="run">
            <h4>Models</h4>
            <ul class="shares">
                @for (s of run.segments; track s.modelKey) {
                <li><span class="swatch" [style.background]="modelColour(s.modelKey)"></span>{{ modelName(s.modelKey) }}<span>{{ percent(s.share) }}</span></li>
                }
            </ul>
        </ng-template>

        <ng-template #createsList>
            @if (creates.length) {
            <h4>Outputs it creates</h4>
            <ul class="handovers">
                @for (c of creates; track c.output.key) {
                <li><span class="swatch round" [style.background]="modelColour(c.output.modelKey)"></span><span class="name">{{ c.output.name }}</span>
                    <span class="muted">{{ count(c.items) }} a year</span></li>
                }
            </ul>
            }
        </ng-template>

        <ng-template #links let-kinds="kinds">
            @if (!handoversIn.length && !handoversOut.length) {<p class="muted">None in the models shown.</p>}
            <ul class="handovers">
                @for (h of handoversIn; track h.handover.key) {
                <li><span class="dir">From</span><button type="button" class="link" (click)="focus(h.run)">{{ h.run.name }}</button>
                    <span class="muted">{{ count(h.handover.items) }} a year@if (kinds) { · <span class="line small" [class.dashed-line]="interfaceKind(h.handover) === 'manual'" [style.border-color]="interfaceColours[interfaceKind(h.handover)]"></span>{{ interfaceKind(h.handover) === 'automated' ? 'automated' : interfaceKind(h.handover) === 'manual' ? 'by hand' : 'partly automated' }}}</span></li>
                }
                @for (h of handoversOut; track h.handover.key) {
                <li><span class="dir">To</span><button type="button" class="link" (click)="focus(h.run)">{{ h.run.name }}</button>
                    <span class="muted">{{ count(h.handover.items) }} a year@if (kinds) { · <span class="line small" [class.dashed-line]="interfaceKind(h.handover) === 'manual'" [style.border-color]="interfaceColours[interfaceKind(h.handover)]"></span>{{ interfaceKind(h.handover) === 'automated' ? 'automated' : interfaceKind(h.handover) === 'manual' ? 'by hand' : 'partly automated' }}}</span></li>
                }
            </ul>
        </ng-template>
        }
    </mat-sidenav-content>

    <mat-sidenav #rightdrawer mode="side" [opened]="false" position="end" class="report-drawer">
        <button mat-icon-button class="report-close" aria-label="Close the report" matTooltip="Close the report" (click)="rightdrawer.close()">
            <mat-icon>close</mat-icon>
        </button>
        @if (rightdrawer.opened) {
        @if (lensKey === 'people' && workforce) {
        <app-workforce-report [linqset]="linqset" [prebuilt]="workforce" [baseline]="baselineWorkforce"></app-workforce-report>
        } @else if (lensKey === 'systems' && systemsReport) {
        <app-systems-report [linqset]="linqset" [prebuilt]="systemsReport" [baseline]="baselineSystems"></app-systems-report>
        }
        }
    </mat-sidenav>
</mat-sidenav-container>
